<template>
  <div ref="container" class="dx-flow"></div>
</template>

<script>
import * as d3 from 'd3'

export default {
  name: 'DxFlow',
  props: {
    encounterCodes: { type: Array, default: () => [] },
    claimCodes: { type: Array, default: () => [] },
    eobCodes: { type: Array, default: () => [] },
    conditionIndex: { type: Object, default: () => ({}) },
  },
  watch: {
    encounterCodes() { this.render() },
    claimCodes() { this.render() },
    eobCodes() { this.render() },
  },
  mounted() {
    this.render()
  },
  methods: {
    render() {
      const el = this.$refs.container
      if (!el) return
      d3.select(el).selectAll('*').remove()

      const encSet = new Set(this.encounterCodes)
      const clmSet = new Set(this.claimCodes)
      const eobSet = new Set(this.eobCodes)
      const allCodes = [...new Set([...encSet, ...clmSet, ...eobSet])].sort()

      if (!allCodes.length) return

      // Classify each code
      const rows = allCodes.map(code => {
        const inEnc = encSet.has(code)
        const inClm = clmSet.has(code)
        const inEob = eobSet.has(code)
        const desc = this.conditionIndex[code]?.SHORT_DESCRIPTION || ''
        let category
        if (inEnc && inClm && inEob) category = 'all3'
        else if (inEnc && inClm) category = 'enc_clm'
        else if (inClm && inEob) category = 'clm_eob'
        else if (inEnc && inEob) category = 'enc_eob'
        else if (inEnc) category = 'enc_only'
        else if (inClm) category = 'clm_only'
        else category = 'eob_only'
        return { code, desc, inEnc, inClm, inEob, category }
      })

      // Sort: all3 first, then enc_clm, clm_eob, enc_only, clm_only, eob_only
      const order = ['all3', 'enc_clm', 'enc_eob', 'clm_eob', 'enc_only', 'clm_only', 'eob_only']
      rows.sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category) || a.code.localeCompare(b.code))

      const colors = {
        all3: '#059669',
        enc_clm: '#7C3AED',
        enc_eob: '#0891B2',
        clm_eob: '#DC2626',
        enc_only: '#3B82F6',
        clm_only: '#D97706',
        eob_only: '#6B7280',
      }

      const fontSize = 11
      const rowH = 20
      const headerH = 30
      const colW = 90
      const codeW = 70
      const descW = 240
      const margin = { top: 4, left: 4, right: 4 }
      const totalW = margin.left + colW + colW + colW + codeW + descW + margin.right
      const totalH = headerH + rows.length * rowH

      const svg = d3.select(el)
        .append('svg')
        .attr('width', totalW)
        .attr('height', totalH)
        .style('font-family', 'Inter, ui-sans-serif, system-ui, sans-serif')

      // Column positions (centers)
      const cx = [
        margin.left + colW / 2,
        margin.left + colW + colW / 2,
        margin.left + colW * 2 + colW / 2,
      ]
      const codeX = margin.left + colW * 3 + 8
      const descX = codeX + codeW

      // Headers
      const headers = [
        { label: 'Encounter', x: cx[0], bg: '#DBEAFE', border: '#3B82F6' },
        { label: 'Claim', x: cx[1], bg: '#FEF3C7', border: '#F59E0B' },
        { label: 'EOB', x: cx[2], bg: '#D1FAE5', border: '#059669' },
      ]
      headers.forEach(h => {
        svg.append('rect')
          .attr('x', h.x - 34)
          .attr('y', 2)
          .attr('width', 68)
          .attr('height', 20)
          .attr('rx', 3)
          .attr('fill', h.bg)
          .attr('stroke', h.border)
          .attr('stroke-width', 1)
        svg.append('text')
          .attr('x', h.x)
          .attr('y', 15)
          .attr('text-anchor', 'middle')
          .attr('font-size', fontSize)
          .attr('font-weight', 600)
          .attr('fill', '#374151')
          .text(h.label)
      })

      svg.append('text')
        .attr('x', codeX)
        .attr('y', 15)
        .attr('font-size', fontSize)
        .attr('font-weight', 600)
        .attr('fill', '#6B7280')
        .text('ICD-10')

      svg.append('text')
        .attr('x', descX)
        .attr('y', 15)
        .attr('font-size', fontSize)
        .attr('font-weight', 600)
        .attr('fill', '#6B7280')
        .text('Description')

      // Rows
      const dotR = 5
      rows.forEach((r, i) => {
        const y = headerH + i * rowH + rowH / 2
        const color = colors[r.category]

        // Alternating row background
        if (i % 2 === 0) {
          svg.append('rect')
            .attr('x', 0)
            .attr('y', y - rowH / 2)
            .attr('width', totalW)
            .attr('height', rowH)
            .attr('fill', '#F9FAFB')
        }

        // Dots for presence
        const positions = [
          { present: r.inEnc, cx: cx[0] },
          { present: r.inClm, cx: cx[1] },
          { present: r.inEob, cx: cx[2] },
        ]
        positions.forEach(p => {
          if (p.present) {
            svg.append('circle')
              .attr('cx', p.cx)
              .attr('cy', y)
              .attr('r', dotR)
              .attr('fill', color)
              .attr('opacity', 0.8)
          } else {
            svg.append('circle')
              .attr('cx', p.cx)
              .attr('cy', y)
              .attr('r', dotR - 1)
              .attr('fill', 'none')
              .attr('stroke', '#D1D5DB')
              .attr('stroke-width', 1)
          }
        })

        // Lines connecting filled dots
        const filled = positions.filter(p => p.present)
        if (filled.length >= 2) {
          svg.append('line')
            .attr('x1', filled[0].cx + dotR)
            .attr('y1', y)
            .attr('x2', filled[filled.length - 1].cx - dotR)
            .attr('y2', y)
            .attr('stroke', color)
            .attr('stroke-width', 1.5)
            .attr('opacity', 0.4)
        }

        // Code
        svg.append('text')
          .attr('x', codeX)
          .attr('y', y + 3)
          .attr('font-size', fontSize)
          .attr('fill', color)
          .attr('font-weight', 500)
          .text(r.code)

        // Description (truncated)
        const maxDescLen = 32
        const truncDesc = r.desc.length > maxDescLen ? r.desc.slice(0, maxDescLen) + '…' : r.desc
        svg.append('text')
          .attr('x', descX)
          .attr('y', y + 3)
          .attr('font-size', fontSize)
          .attr('fill', '#6B7280')
          .text(truncDesc)
      })

      // (no summary line)
    },
  },
}
</script>

<style scoped>
.dx-flow {
  overflow-x: auto;
}
</style>
